/**
 * Display text for ids the server sends: roles (from @handshake/config), cards, phases and win
 * conditions. Pure lookups: no game logic.
 */
import type { PresetId, RoleId } from "@handshake/config";
import { PRESETS, ROLES } from "@handshake/config/catalog";
import type { CardId, Outcome, PhaseKind, PinIcon } from "./types.ts";

export function roleName(role: string | null | undefined): string {
  if (!role) return "Unknown";
  return (ROLES as Record<string, { name: string }>)[role]?.name ?? role;
}

export function roleAbility(role: RoleId): string {
  return ROLES[role].ability;
}

export function presetName(id: string | null | undefined): string | null {
  if (!id) return null;
  return (PRESETS as Record<string, { name: string }>)[id]?.name ?? id;
}

/** Every preset id, in the design doc's order (§12.4). */
export const PRESET_LIST = Object.keys(PRESETS) as PresetId[];

export function presetOf(id: PresetId) {
  return PRESETS[id];
}

export const CARD_LABELS: Readonly<Record<CardId, string>> = {
  chat: "Chat",
  seal: "Seal",
  siphon: "Siphon",
  poison: "Poison",
  inoculate: "Inoculate",
  audit: "Audit",
  tail: "Tail",
  plant: "Plant",
  bug: "Bug",
  confide: "Confide",
};

/** One-line card texts (§7, §11). */
export const CARD_TEXT: Readonly<Record<CardId, string>> = {
  chat: "Nothing happens. Pure cover.",
  seal: "Cancels every Syndicate card your partner plays in this handshake.",
  siphon: "+1 Leak if your partner is a Delegate.",
  poison: "Your partner dies at the next Dusk unless cured. Uses a shared vial.",
  inoculate: "Cures your partner of Poison; they ignore Siphons this round.",
  audit: "At Dusk: how many Syndicate cards your partner played this round.",
  tail: "At Dusk: everyone your partner shook hands with this round.",
  plant: "Your partner registers as an Agent this round.",
  bug: "Your partner's cards are voided and their report scrambled.",
  confide: "Once per game: you and your partner learn each other's true team.",
};

export function cardLabel(card: CardId): string {
  return CARD_LABELS[card] ?? card;
}

export const PIN_LABELS: Readonly<Record<PinIcon, string>> = {
  siphon: "Siphon",
  poison: "Poison",
  avoid: "Avoid",
};

export const PHASE_TITLES: Readonly<Record<PhaseKind, string>> = {
  lobby: "Lobby",
  arrival: "Arrival",
  mingle: "Mingle",
  dusk: "Dusk",
  discussion: "Council · Discussion",
  nominations: "Council · Nominations",
  defence: "Council · Defence",
  ballot: "Council · Ballot",
  replacement: "Council · Replacement",
  ended: "The End",
};

export function teamName(team: string | null | undefined): string {
  return team === "syndicate" ? "Syndicate" : team === "delegate" ? "Delegates" : "—";
}

export function outcomeText(outcome: Outcome): string {
  switch (outcome.condition) {
    case "leak-limit":
      return "The Leak track reached its limit.";
    case "parity":
      return "The Agents reached parity with the Delegates.";
    case "final-tally":
      return "Agents survived the final round with the Leak track at least half full.";
    case "agents-exiled":
      return "Every Agent was exiled.";
    case "final-tally-failed":
      return "Agents survived, but the Leak track stayed below half full.";
    default:
      return "";
  }
}

export const BALLOT_OUTCOMES: Readonly<Record<string, string>> = {
  exiled: "exiled",
  tie: "A tie: nobody is exiled.",
  "not-more-than-none": "Not more votes than No exile: nobody is exiled.",
  "below-threshold": "Too few votes: nobody is exiled.",
  "no-votes": "No votes: nobody is exiled.",
  "no-nominees": "No nominees: nobody is exiled.",
};
